import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ToastList } from '@/components/ui/Toast';

/**
 * ToastContext — Gerencia notificações toast na aplicação.
 *
 * Suporta variantes: success, error, info, warning.
 * Auto-dismiss após 3 segundos. Múltiplos toasts empilham no topo da tela.
 */

const AUTO_DISMISS_MS = 3_000;

export interface Toast {
  id: string;
  message: string;
  variant: 'success' | 'error' | 'info' | 'warning';
}

interface ToastContextType {
  showToast(message: string, variant?: Toast['variant']): void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: Toast['variant'] = 'info') => {
      counterRef.current += 1;
      const id = `toast-${counterRef.current}-${Date.now()}`;

      const toast: Toast = { id, message, variant };
      setToasts((prev) => [...prev, toast]);

      setTimeout(() => {
        removeToast(id);
      }, AUTO_DISMISS_MS);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastList toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast deve ser usado dentro de ToastProvider');
  }
  return context;
}
